import { useMemo } from 'react';

import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  contentQueryKeys,
  fetchCatalogCategories,
  fetchCatalogItems,
} from '@/features/content/api';
import type { CatalogItemSummary } from '@/features/content/types';
import { headerMenuGroups } from '@/features/home/data';
import type { HeaderMenuGroup, SiteLink } from '@/features/home/types';
import { useAppLocale } from '@/i18n/useAppLocale';
import { routePaths, type CatalogCategorySlug } from '@/routes/routeRegistry';

const PRODUCT_HEADER_CATEGORY_LIMIT = 4;

const PRODUCT_HEADER_GROUP_IDS = new Set([
  'co2-natural-refrigerant',
  'refrigeration-system',
  'energy-solution',
  'mechanical-hvac',
]);

const createCategoryLink = (categorySlug: CatalogCategorySlug, label: string) => {
  const href = routePaths.catalogCategory(categorySlug);

  return {
    href,
    label,
    to: href,
  } satisfies SiteLink;
};

const createCatalogItemLink = (item: CatalogItemSummary) => {
  const href = routePaths.catalogDetail(item.slug);

  return {
    href,
    label: item.title,
    to: href,
  } satisfies SiteLink;
};

export const useSiteMenuGroups = () => {
  const { t } = useTranslation('navigation');
  const { locale } = useAppLocale();
  const catalogCategoriesQuery = useQuery({
    queryKey: [...contentQueryKeys.catalogCategories, locale],
    queryFn: fetchCatalogCategories,
    staleTime: 60_000,
  });
  const visibleProductCategories = useMemo(() => {
    return (
      catalogCategoriesQuery.data
        ?.filter((category) => category.isVisible)
        .slice(0, PRODUCT_HEADER_CATEGORY_LIMIT) ?? []
    );
  }, [catalogCategoriesQuery.data]);

  const catalogItemQueries = useQueries({
    queries: visibleProductCategories.map((category) => ({
      queryKey: [...contentQueryKeys.catalogItems(category.slug), locale],
      queryFn: () => fetchCatalogItems(category.slug),
      staleTime: 60_000,
    })),
  });

  return useMemo<HeaderMenuGroup[]>(() => {
    const translateLabel = (label: string) => {
      return t(`labels.${label}`);
    };

    const translateLink = <TLink extends SiteLink>(link: TLink): TLink => {
      return {
        ...link,
        label: translateLabel(link.label),
      };
    };

    const translateHeaderMenuGroup = (group: HeaderMenuGroup): HeaderMenuGroup => {
      const descriptionKey = group.id.replace(/-([a-z])/g, (_, letter: string) =>
        letter.toUpperCase(),
      );

      return {
        ...group,
        description: t(`menuDescriptions.${descriptionKey}`),
        descriptionTitle: translateLabel(group.descriptionTitle),
        hoverItems: group.hoverItems.map(translateLink),
        items: group.items.map((item) => {
          const translatedItem = translateLink(item);

          return item.children?.length
            ? {
                ...translatedItem,
                children: item.children.map(translateLink),
              }
            : translatedItem;
        }),
        label: translateLabel(group.label),
      };
    };

    const staticProductGroups = new Map(
      headerMenuGroups
        .filter((group) => PRODUCT_HEADER_GROUP_IDS.has(group.id))
        .map((group) => [group.id, translateHeaderMenuGroup(group)]),
    );
    const staticUtilityGroups = headerMenuGroups
      .filter((group) => !PRODUCT_HEADER_GROUP_IDS.has(group.id))
      .map(translateHeaderMenuGroup);
    const productGroups =
      visibleProductCategories.length > 0
        ? visibleProductCategories.map((category, index) => {
            const fallbackGroup = staticProductGroups.get(category.slug);
            const href = routePaths.catalogCategory(category.slug);
            const categoryLabel = category.label;
            const fallbackItems = [createCategoryLink(category.slug, categoryLabel)];
            const itemQuery = catalogItemQueries[index];
            const dynamicItems = (itemQuery.data ?? []).map(createCatalogItemLink);
            const menuItems = itemQuery.isSuccess
              ? dynamicItems.length > 0
                ? dynamicItems
                : fallbackItems
              : (fallbackGroup?.hoverItems ?? fallbackItems);

            return {
              id: category.slug,
              label: categoryLabel,
              href,
              to: href,
              descriptionTitle: categoryLabel,
              description:
                fallbackGroup?.description ??
                t('dynamicCategoryDescription', { category: categoryLabel }),
              hoverItems: menuItems,
              items: menuItems,
            } satisfies HeaderMenuGroup;
          })
        : headerMenuGroups
            .filter((group) => PRODUCT_HEADER_GROUP_IDS.has(group.id))
            .map(translateHeaderMenuGroup);

    return [...productGroups, ...staticUtilityGroups];
  }, [catalogItemQueries, t, visibleProductCategories]);
};
